import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik, getIn, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';

import { withStyles } from '~/assets/withStyles';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import EmptyState from '~/components/core/EmptyState';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateFormatWithoutTZChange } from '~/DateTimeUtils';
import { isLocaleRegionIsUs } from '~/Utils/regionUtils';

import { CATASTROPHE_TYPES, COUNTRY_TO_STATE_MAP } from '../../Types';
import { reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import { SortableTable } from '../core';
import { PencilIcon } from '../icons';
import InlineIconButton from '../InlineIconButton';
import LoadingIndicator from '../LoadingIndicator';
import useOrganization from '../OrganizationContext';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MultiSelectTextFieldFormik,
  useSetDefaultFieldsOnChange,
} from '../TextFieldFormik';
import useDataFetcher from '../useDataFetcher';

import { useCatEventsPagination } from './hooks/useCatEventsPagination';
import OperationsBreadcrumbs from './OperationsBreadcrumbs';
import { useSysconfig } from './SystemConfigurationScreen';

import styles, { useStyles } from '../../assets/styles';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_SORT = { id: 'start_date', order: 'desc' };
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const CatEventsTable = () => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const [addCatEventDialogOpen, setAddCatEventDialogOpen] = useState(false);
  const [catEventToEdit, setCatEventToEdit] = useState(null);
  const [catMarkDisablePopupOpen, setCatMarkDisablePopupOpen] = useState(false);
  const [catEnablingSubmitting, setCatEnablingSubmitting] = useState(false);
  const [sortByColumn, setSortByColumn] = useState(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const sortString = `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`;

  const {
    isLoading: catConfigLoading,
    isError: catConfigError,
    data: catConfig,
    reloadData: reloadCatConfig,
  } = useDataFetcher(`/sysconfig/api/v1/organizations/${organization.id}/cat_config`);

  const {
    pagination,
    pageResults: catEvents,
    extraData,
    isLoading: catEventsLoading,
    isError: catEventsError,
    reload: reloadCatEvents,
    refreshPageData: refreshCatEventsPage,
    updatePageResult: updateCatEventInPage,
  } = useCatEventsPagination({
    organizationId: organization.id,
    sortByColumn: sortString,
    nameSearch,
  });

  const { reloadOperationalDetails } = useOrganization();

  const debouncedSetNameSearch = useMemo(() => debounce((value) => setNameSearch(value), SEARCH_DEBOUNCE_MS), []);

  useEffect(() => () => debouncedSetNameSearch.cancel(), [debouncedSetNameSearch]);

  // Reset to first page when the search changes so the user doesn't land past the new ID list.
  useEffect(() => {
    pagination.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameSearch]);

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    debouncedSetNameSearch(value);
  };

  const { page, rowsPerPage, totalCount, setPage, setRowsPerPage } = pagination;

  const handleSortByColumn = useCallback(
    (_event, { id, order }) => {
      setSortByColumn({ id, order });
      setPage(0);
    },
    [setPage]
  );

  const paginationProps = useMemo(
    () => ({
      page,
      count: totalCount,
      rowsPerPage,
      onChangePage: (_event, newPage) => setPage(newPage),
      onChangeRowsPerPage: (event) => setRowsPerPage(parseInt(event.target.value, 10)),
      rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    }),
    [page, totalCount, rowsPerPage, setPage, setRowsPerPage]
  );

  if (catConfigLoading || catConfigError) {
    return <LoadingIndicator isError={catConfigError} />;
  }

  const enableCatEvents = async () => {
    try {
      setCatEnablingSubmitting(true);
      const requestedCatEnableStatus = { status: !catConfig.enable_mark_as_catastrophe };
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/cat_config`, requestedCatEnableStatus);
      await reloadCatConfig();
      await reloadCatEvents();
      await reloadOperationalDetails();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setCatEnablingSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setAddCatEventDialogOpen(false);
    setCatEventToEdit(null);
  };

  const handleOpenDialog = (catEvent) => {
    if (catEvent) {
      setCatEventToEdit(catEvent);
    }
    setAddCatEventDialogOpen(true);
  };

  const handleCreateCatEvent = async (values) => {
    try {
      await axios.post(`/sysconfig/api/v1/organizations/${organization.id}/cat_events`, values);
      await reloadCatEvents();
      handleCloseDialog();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const handleUpdateCatEvent = async (values, catEventId) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/cat_events/${catEventId}`, values);
      await reloadCatEvents();
      handleCloseDialog();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const handleToggleHidden = async (row) => {
    const nextValues = { ...row, is_hidden: !row.is_hidden };
    updateCatEventInPage(nextValues);
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/cat_events/${row.id}`, nextValues);
      await refreshCatEventsPage();
    } catch (error) {
      updateCatEventInPage(row);
      await reportAxiosError(error);
    }
  };

  let columnData = [
    { id: 'name', label: 'Cat Name' },
    { id: 'code', label: 'Cat Code' },
    { id: 'sync_key', label: 'Sync Key' },
    { id: 'pcs_number', label: 'PCS' },
    {
      id: 'types',
      label: 'Type',
      disableSort: true,
      specialCell: (data) => (data.types ? data.types.join(', ') : ''),
    },
    {
      id: 'start_date',
      label: 'Start Date',
      specialCell: (date) => (date.start_date ? serverDateFormatWithoutTZChange(date.start_date) : ''),
    },
    {
      id: 'end_date',
      label: 'End Date',
      specialCell: (date) => (date.end_date ? serverDateFormatWithoutTZChange(date.end_date) : ''),
    },
    {
      id: 'us_states',
      label: 'Location',
      disableSort: true,
      specialCell: (data) => (data.us_states ? data.us_states.join(', ') : ''),
    },
    {
      id: 'is_hidden',
      label: 'Show',
      specialCell: (data) => (
        <span>
          <Switch checked={!data.is_hidden} onChange={() => handleToggleHidden(data)} />
        </span>
      ),
    },
    { id: 'count', label: 'Number of claims', disableSort: true },
    {
      id: 'edit',
      label: 'Actions',
      disableSort: true,
      specialCell: (data) => (
        <InlineIconButton
          tooltipTitle="Edit"
          icon={PencilIcon}
          className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
          onClick={() => handleOpenDialog(data)}
        />
      ),
    },
  ];

  if (!isLocaleRegionIsUs()) {
    columnData = columnData.filter((column) => column.id !== 'us_states');
  }

  const hasIncidentsWithCat = Boolean(extraData?.has_incidents_with_cat);

  return (
    <>
      <CardDialog noCardTitle>
        <OperationsBreadcrumbs currentTab="Catastrophic Events" />
        <CardDialog title="CATASTROPHIC EVENTS">
          <Typography variant="body1">
            Add and manage catastrophic events for the ability to mark claim as catastrophe. You can choose whether to
            display the catastrophe in the marking catastrophe drop-down list.
          </Typography>
          <div className={classes.spaceBetweenButtonsContainer}>
            <span>
              <Switch
                checked={catConfig.enable_mark_as_catastrophe}
                disabled={catEnablingSubmitting}
                onChange={
                  catConfig.enable_mark_as_catastrophe ? () => setCatMarkDisablePopupOpen(true) : enableCatEvents
                }
              />
              <span>Enable “Mark as catastrophic event” on Summary details</span>
            </span>
            <Button color="primary" onClick={() => handleOpenDialog()}>
              <AddIcon className={classes.leftButtonIcon} />
              Add New Catastrophic Event
            </Button>
          </div>
          <div className="my-20 grid grid-cols-3 gap-32">
            <TextField label="Search Catastrophic Events" value={searchInput} onChange={handleSearchInputChange} />
          </div>
          {catEventsError ? (
            <LoadingIndicator isError />
          ) : catEventsLoading && catEvents.length === 0 ? (
            <SkeletonTable rowsCount={pagination.rowsPerPage} columnsCount={columnData.length} />
          ) : pagination.totalCount === 0 ? (
            <EmptyState />
          ) : (
            <SortableTable
              columns={columnData}
              rows={catEvents}
              paginationProps={paginationProps}
              defaultOrderColumn={columnData.findIndex((c) => c.id === sortByColumn.id)}
              order={sortByColumn.order}
              onSortByColumn={handleSortByColumn}
              keepRowsOrder
              stickyHeader
            />
          )}
          {pagination.resultsTruncated && (
            <Typography variant="caption" color="error">
              Showing the first {pagination.totalCount} results. Refine your search to see the rest.
            </Typography>
          )}
        </CardDialog>
        {addCatEventDialogOpen && (
          <AddAndEditCatEventDialogStyled
            onCreate={handleCreateCatEvent}
            onUpdate={handleUpdateCatEvent}
            onClose={handleCloseDialog}
            catEvent={catEventToEdit}
          />
        )}
        {catMarkDisablePopupOpen && (
          <DisableCatPopup
            onClose={() => setCatMarkDisablePopupOpen(false)}
            enableCatEvents={enableCatEvents}
            hasIncidentsWithCat={hasIncidentsWithCat}
          />
        )}
      </CardDialog>
    </>
  );
};

const DisableCatPopup = (props) => {
  const { onClose, enableCatEvents, hasIncidentsWithCat } = props;
  const { classes } = useStyles();

  return (
    <CardDialog title={'Disable “Mark as catastrophic event"'} isDialog maxWidth="xs" fullWidth onClose={onClose}>
      {hasIncidentsWithCat ? (
        <>
          <Typography>
            Disabling &quot;Mark as catastrophic event&quot; is not available with open incidents which were previously
            marked as catastrophic event.
          </Typography>
          <div className={classes.buttonsContainer}>
            <Button variant="contained" color="primary" onClick={onClose} className={classes.button}>
              Ok
            </Button>
          </div>
        </>
      ) : (
        <>
          <Typography>
            Are you sure you want to disable &quot;Mark as catastrophic event&quot;? Once you disable this feature
            marking an incident as a catastrophic event will not be available in the summary page.
          </Typography>
          <div className={classes.buttonsContainer}>
            <Button variant="contained" onClick={onClose} className={classes.cancelButtonWithRoundMargin}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                enableCatEvents();
                onClose();
              }}
              className={classes.button}
            >
              Yes, disable
            </Button>
          </div>
        </>
      )}
    </CardDialog>
  );
};

DisableCatPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  enableCatEvents: PropTypes.func.isRequired,
  hasIncidentsWithCat: PropTypes.bool.isRequired,
};

const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const generateSyncKey = (values) => {
  const parts = ['cat'];
  if (values.start_date) {
    parts.push(moment(values.start_date).format('YYYY'));
  }
  if (values.types && values.types.length > 0) {
    parts.push(slugify(values.types[0]));
  }
  if (values.name) {
    parts.push(slugify(values.name));
  }
  return parts.join('_');
};

const SyncKeyAutoFill = ({ isAddMode }) => {
  const { values } = useFormikContext();

  useSetDefaultFieldsOnChange(
    generateSyncKey(values),
    { sync_key: isAddMode ? generateSyncKey(values) : getIn(values, 'sync_key') },
    undefined,
    undefined,
    true
  );

  return null;
};

SyncKeyAutoFill.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
};

const AddAndEditCatEventDialog = (props) => {
  const { onClose, onCreate, onUpdate, catEvent, classes } = props;

  let initialValues = {
    name: '',
    code: '',
    sync_key: '',
    pcs_number: '',
    types: [],
    start_date: '',
    end_date: '',
    us_states: [],
    is_hidden: false,
  };

  if (catEvent) {
    initialValues = { ...initialValues, ...catEvent };
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        name: Yup.string().required('Required'),
        code: Yup.string(),
        sync_key: Yup.string()
          .matches(/^[a-z0-9_]*$/, 'Only lowercase letters, digits, and underscores')
          .nullable(),
        pcs_number: Yup.number().nullable(),
        types: Yup.array(),
        start_date: Yup.date().nullable(),
        end_date: Yup.date().nullable(),
        us_states: Yup.array(),
        is_hidden: Yup.boolean(),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          if (catEvent) {
            await onUpdate(values, catEvent.id);
          } else {
            await onCreate(values);
          }
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {(formikProps) => {
        const { values, isSubmitting, handleSubmit, setFieldValue } = formikProps;

        return (
          <CardDialog
            title={catEvent ? 'Edit Catastrophic Event' : 'Add New Catastrophic Event'}
            isDialog
            preventClose={isSubmitting}
            maxWidth="sm"
            fullWidth
            onClose={onClose}
          >
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <TextFieldFormik
                  id="name"
                  label="Catastrophic Event Name"
                  fullWidth
                  className={classes.textField}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={7} />
              <Grid item xs={5}>
                <TextFieldFormik
                  id="code"
                  label="Cat Code (internal)"
                  className={classes.textField}
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={7} />
              <Grid item xs={5}>
                <TextFieldFormik
                  id="pcs_number"
                  label="PCS"
                  className={classes.textField}
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={7} />
              <Grid item xs={5}>
                <MultiSelectTextFieldFormik
                  id="types"
                  label="Types"
                  className={classes.textField}
                  fullWidth
                  options={CATASTROPHE_TYPES}
                  withOptionChips
                  renderValue={() => ''}
                />
              </Grid>
              <Grid item xs={7} />
              <Grid item xs={5}>
                <DatePickerTextFieldFormik
                  id="start_date"
                  label="Start Date"
                  className={classes.textField}
                  disableFuture
                  fullWidth
                  disabled={isSubmitting}
                  clearable
                />
              </Grid>
              <Grid item xs={5}>
                <DatePickerTextFieldFormik
                  id="end_date"
                  label="End Date"
                  className={classes.textField}
                  disableFuture
                  fullWidth
                  disabled={isSubmitting}
                  clearable
                />
              </Grid>
              {!catEvent && <SyncKeyAutoFill isAddMode />}
              <Grid item xs={10}>
                <TextFieldFormik
                  id="sync_key"
                  label="Sync Key"
                  className={classes.textField}
                  fullWidth
                  disabled={isSubmitting || !!catEvent}
                />
              </Grid>
              {isLocaleRegionIsUs() && (
                <>
                  <Grid item xs={5}>
                    <MultiSelectTextFieldFormik
                      id="us_states"
                      label="States"
                      className={classes.textField}
                      fullWidth
                      options={Object.keys(COUNTRY_TO_STATE_MAP['US'])
                        .filter((value) => value !== '')
                        .sort((s1, s2) => COUNTRY_TO_STATE_MAP['US'][s1].localeCompare(COUNTRY_TO_STATE_MAP['US'][s2]))}
                      renderOption={(option) => COUNTRY_TO_STATE_MAP['US'][option]}
                      renderValue={() => ''}
                      withOptionChips
                    />
                  </Grid>
                  <Grid item xs={7} />
                </>
              )}
              <Grid item xs={12}>
                <Switch onChange={() => setFieldValue('is_hidden', !values.is_hidden)} checked={!values.is_hidden} />
                <span>
                  Show the catastrophe in the dropdown list under <i>Mark as Catastrophic event</i>
                </span>
              </Grid>
              <div className={classes.buttonsContainer}>
                <CancelButton disabled={isSubmitting} onClick={onClose} />
                <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                  Save
                </Button>
              </div>
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

AddAndEditCatEventDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  catEvent: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

const AddAndEditCatEventDialogStyled = withStyles(AddAndEditCatEventDialog, styles);

export default CatEventsTable;
